import React from 'react';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ExternalLink, Star } from 'lucide-react';
interface PlaceDetailCardProps {
  title: string;
  icon: React.ReactNode;
  children: React.ReactNode;
  className?: string;
}
export const PlaceDetailCard: React.FC<PlaceDetailCardProps> = ({
  title,
  icon,
  children,
  className = ""
}) => <Card className={`overflow-hidden hover:shadow-lg transition-shadow duration-300 ${className}`}>
    <div className="p-6">
      <div className="flex items-center gap-3 mb-4">
        <div className="p-2 bg-primary/10 rounded-lg text-primary">
          {icon}
        </div>
        <h2 className="text-xl font-semibold">{title}</h2>
      </div>
      {children}
    </div>
  </Card>;
export const RatingDisplay: React.FC<{
  rating: number | string | null;
}> = ({
  rating
}) => {
  if (!rating) return null;
  const numRating = typeof rating === 'string' ? parseFloat(rating) : rating;
  const fullStars = Math.floor(numRating);
  const hasHalfStar = numRating % 1 >= 0.5;
  return <div className="flex items-center gap-2">
      <div className="flex">
        {[...Array(5)].map((_, i) => <Star key={i} className={`w-4 h-4 ${i < fullStars ? 'text-yellow-400 fill-yellow-400' : i === fullStars && hasHalfStar ? 'text-yellow-400 fill-yellow-400/50' : 'text-gray-300'}`} />)}
      </div>
      <span className="text-sm font-medium">{numRating}/5</span>
    </div>;
};
export const OfficialWebsiteButton: React.FC<{
  url: string;
  label?: string;
}> = ({
  url,
  label = 'Official Website'
}) => {
  const displayUrl = url.replace(/^https?:\/\//, '');
  const truncatedUrl = displayUrl.length > 25 ? displayUrl.substring(0, 25) + '...' : displayUrl;
  return <Button variant="outline" className="w-full justify-start gap-3 h-12 border-primary/20 hover:border-primary/40 hover:bg-primary/5" onClick={() => window.open(url, '_blank', 'noopener,noreferrer')}>
      <div className="p-1.5 bg-primary/10 rounded">
        <ExternalLink className="w-4 h-4 text-primary" />
      </div>
      <div className="flex flex-col items-start min-w-0 flex-1">
        <span className="font-medium text-gray-800">{label}</span>
        <span className="text-xs text-muted-foreground truncate w-full">
          {truncatedUrl}
        </span>
      </div>
    </Button>;
};
export const AddressBadge: React.FC<{
  address: string;
  label?: string;
}> = ({
  address,
  label = 'Address'
}) => {
  const truncatedAddress = address.length > 40 ? address.substring(0, 40) + '...' : address;
  return <Button variant="outline" className="w-full justify-start gap-3 h-12 border-dashed hover:border-solid hover:bg-accent/50" onClick={() => {
    const mapsUrl = `https://maps.google.com/?q=${encodeURIComponent(address)}`;
    window.open(mapsUrl, '_blank', 'noopener,noreferrer');
  }}>
      <div className="p-1.5 bg-accent/10 rounded">
        📍
      </div>
      <div className="flex flex-col items-start min-w-0 flex-1">
        <span className="font-medium">{label}</span>
        <span className="text-xs text-muted-foreground truncate w-full">
          {truncatedAddress}
        </span>
      </div>
    </Button>;
};